import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';
import { useReactiveVar } from '@apollo/client';
import {
  Box,
  Chip,
  CircularProgress,
  ColorEnum,
  COLORS,
  Flex,
  FormControl,
  FormHelperText,
  Paper,
  TextField,
  Typography,
} from '@dripcapital/dripui-v1';
import LinearProgress from '@mui/material/LinearProgress';
import FolderOpen from '@mui/icons-material/FolderOpen';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  File as UploadFile,
  postValidateDocuments,
  UploadDocument,
  useRemoveFileMutation,
} from 'api';
import { getDigitizationStatus } from 'api/rest/usDocuments';
import { AtomicButton, Alert, Spacer, Modal } from 'atoms';
import { ButtonTypeEnum } from 'enums';
import { AnalyticsContext } from 'shared/context/AnalyticsContext';
import {
  CLIENT_DIGITIZATION_FAILED,
  DAJ_COLORS,
  DIGITIZATION_API_STATES,
  DIGITIZATION_UI_STATES,
  DOCUMENT_TAGS,
  documentsToNumericIds,
  getUnderwritingUserRole,
  handleError,
  normalizeReasons,
  trackUnderwritingMixpanelEvent,
  humanFileSize,
  renameFile,
  TOKEN,
} from 'utils';
import { saveAs } from 'file-saver';
import { appDataVar } from 'config/server';
import { FormikProps } from 'formik';

const COI_POLL_INITIAL_DELAY_MS = 1000;
const COI_POLL_INTERVAL_MS = 5000;
const COI_POLL_MAX_MS = 180000;
// const UW_VALIDATION_COUNT_KEY = '__uwFinancialValidationCount';
// const UW_VALIDATION_LOCK_KEY = '__uwFinancialValidationInProgress';

type Props = {
  leadId: string;
  tag: string;
  files: UploadFile[];
  uploadUrl: string;
  buttonTitle: string;
  title: React.ReactNode;
  descriptionItemsRegular?: boolean;
  descriptionItemsTitle?: React.ReactNode;
  descriptionItems?: string[];
  descriptionItemsLinkNode?: React.ReactNode;
  showChevron?: boolean;
  showReviewProgress?: boolean;
  validationFailedReasons?: string[];
  digitizationStatus?: string | null;
  fileLimit: number;
  supportedFiles: string[];
  maxFiles?: number;
  disabled?: boolean;
  enableValidation?: boolean;
  ein?: string;
  refetch: () => void | Promise<void>;
  onBatchUploadStart?: () => void | Promise<void>;
  onTrackUpload?: () => void;
  uploadError?: string;
  dataTestIdPrefix?: string;
  disableDeleteActions?: boolean;
  disableDeleteForVerified?: boolean;
  hideDeleteActions?: boolean;
  enableReuploadForVerified?: boolean;
  onProcessingStateChange?: (isProcessing: boolean) => void;
  onValidationFired?: (fired: boolean) => void;
  autoValidateIfMissingBatchId?: boolean;
  batchId?: number | null;
  formik?: FormikProps<any>;
};

const UploadCardV2: React.FC<Props> = ({
  leadId,
  tag,
  files,
  uploadUrl,
  buttonTitle,
  title,
  descriptionItemsRegular = false,
  descriptionItemsTitle,
  descriptionItems = [],
  descriptionItemsLinkNode,
  showChevron = true,
  showReviewProgress = false,
  validationFailedReasons = [],
  digitizationStatus = null,
  fileLimit,
  supportedFiles,
  maxFiles = 20,
  disabled = false,
  enableValidation = true,
  ein,
  refetch,
  onBatchUploadStart,
  onTrackUpload,
  dataTestIdPrefix,
  disableDeleteActions = false,
  disableDeleteForVerified = false,
  hideDeleteActions = false,
  enableReuploadForVerified = false,
  onProcessingStateChange,
  onValidationFired,
  autoValidateIfMissingBatchId = false,
  batchId = null,
  formik,
}) => {
  const { t } = useTranslation();
  const { mixpanel } = useContext(AnalyticsContext);
  const { isBDMVar: isBDM, isChannelPartnerVar: isCP } =
    useReactiveVar(appDataVar);
  const [cookies] = useCookies([TOKEN]);
  const [removeFileTag] = useRemoveFileMutation();
  const [visibleFiles, setVisibleFiles] = useState<UploadFile[]>(files || []);
  const [isUploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isValidationInProgress, setValidationInProgress] = useState(false);
  const [isHeaderAccordionOpen, setHeaderAccordionOpen] = useState(true);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  // const [localError, setLocalError] = useState(uploadError);
  const [polledStatus, setPolledStatus] = useState('');
  const [polledRejectionReasons, setPolledRejectionReasons] = useState<
    string[]
  >(normalizeReasons(validationFailedReasons));
  const reasonFieldName = `${tag}_reason`;

  const savedReasonCacheValue = sessionStorage.getItem(reasonFieldName) || '';
  const currentValue =
    formik?.values[reasonFieldName] !== undefined
      ? formik?.values[reasonFieldName]
      : savedReasonCacheValue;
  const errorMessage = formik?.errors[reasonFieldName];
  const validateLockRef = useRef(false);
  const latestFilesRef = useRef<UploadFile[]>(files || []);

  const userRole = getUnderwritingUserRole(isBDM, isCP);
  const resolvedTestIdPrefix =
    dataTestIdPrefix || `uw-financial-upload-card-${String(tag || 'unknown')}`;
  const normalizedTag = String(tag || '')
    .trim()
    .toLowerCase();

  const wait = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, ms);
      }),
    []
  );

  const filesSignature = useMemo(
    () =>
      (files || [])
        .map((f) => `${f.documentId ?? ''}:${f.name ?? ''}`)
        .sort()
        .join('|'),
    [files]
  );

  // useEffect(() => {
  //   setLocalError(uploadError);
  // }, [uploadError]);

  useEffect(() => {
    const nextReasons = normalizeReasons(validationFailedReasons);
    if (nextReasons.length > 0) {
      setPolledRejectionReasons(nextReasons);
    }
  }, [digitizationStatus, validationFailedReasons]);

  // useEffect(() => {
  //   const w = window as typeof window & Record<string, unknown>;
  //   const updateValidationLock = (delta: number) => {
  //     const currentCount = Number(w[UW_VALIDATION_COUNT_KEY] || 0);
  //     const nextCount = Math.max(0, currentCount + delta);
  //     w[UW_VALIDATION_COUNT_KEY] = nextCount;
  //     w[UW_VALIDATION_LOCK_KEY] = nextCount > 0;
  //   };
  //   if (isValidationInProgress) {
  //     updateValidationLock(1);
  //   }
  //   return () => {
  //     if (isValidationInProgress) {
  //       updateValidationLock(-1);
  //     }
  //   };
  // }, [isValidationInProgress]);

  useEffect(() => {
    const freshCacheReasonValue = sessionStorage.getItem(reasonFieldName) || '';

    if (freshCacheReasonValue) {
      formik?.setFieldValue(reasonFieldName, freshCacheReasonValue, false);
    } else if (formik?.values[reasonFieldName] === undefined) {
      formik?.setFieldValue(reasonFieldName, '', false);
    }

    return () => {
      formik?.setFieldError(reasonFieldName, undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasonFieldName]);

  const trackDigitizationStage = useCallback(
    (
      area: string,
      action: string,
      result: string,
      extra?: {
        payload?: Record<string, unknown>;
        status?: string;
        batchId?: number | null;
        docCount?: number;
      },
      errorCode?: string
    ) => {
      trackUnderwritingMixpanelEvent(
        mixpanel,
        { leadId, userRole, screen: 'financial' },
        {
          area,
          action,
          result,
          tag: normalizedTag,
          status: extra?.status,
          batchId: extra?.batchId,
          docCount: extra?.docCount,
          errorCode,
          payload: extra?.payload,
        }
      );
    },
    [leadId, mixpanel, normalizedTag, userRole]
  );

  const getTaggedIdsFromLatestFiles = useCallback(() => {
    const acceptedTags = (() => {
      if (tag === DOCUMENT_TAGS.FINANCIALS) {
        return [
          DOCUMENT_TAGS.FINANCIALS,
          DOCUMENT_TAGS.PROFIT_AND_LOSS,
          DOCUMENT_TAGS.BALANCE_SHEET,
        ];
      }
      if (tag === DOCUMENT_TAGS.PROVISIONAL_FINANCIALS) {
        return [
          DOCUMENT_TAGS.PROVISIONAL_FINANCIALS,
          DOCUMENT_TAGS.PROVISIONAL_PL_STATEMENT,
          DOCUMENT_TAGS.PROVISIONAL_BALANCE_SHEET,
        ];
      }
      return [tag];
    })();
    const taggedDocs = (latestFilesRef.current || []).filter((f) =>
      acceptedTags.includes(String(f.tags || ''))
    );
    return documentsToNumericIds(taggedDocs);
  }, [tag]);

  const resolveTaggedIdsWithRetries = useCallback(async () => {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const ids = getTaggedIdsFromLatestFiles();
      if (ids.length > 0) return ids;
      await refetch();
      await wait(700);
    }
    return [] as number[];
  }, [getTaggedIdsFromLatestFiles, refetch, wait]);

  // Runs document digitization validation for the current tag and lead.
  const runValidationByTag = useCallback(async () => {
    if (onValidationFired) {
      onValidationFired(false);
    }
    // Step 1: Bail if validation shouldn't start (no validation, no leadId, or lock is set)
    if (!enableValidation || !leadId || validateLockRef.current) {
      setValidationInProgress(false);
      return;
    }

    // Step 2: Prepare the EIN value and determine if current tag requires it
    const einTrim = (ein || '').trim();
    const tagNeedsEin =
      tag === DOCUMENT_TAGS.COI || tag === DOCUMENT_TAGS.TAX_RETURNS;

    // Step 3: If EIN is required but missing, track and show error, then exit
    if (tagNeedsEin && !einTrim) {
      trackDigitizationStage(
        'validation',
        'blocked',
        'missing_ein',
        undefined,
        'missing-ein'
      );
      handleError(t('us_digitization_ein_required'));
      setValidationInProgress(false);
      return;
    }

    // Step 4: Track that validation is being initiated
    trackDigitizationStage('validation', 'started', 'initiated');

    // Step 5: Attempt to get the document IDs for validation, retrying if necessary
    const ids = await resolveTaggedIdsWithRetries();

    // Step 6: If we couldn't get any document IDs, track and show error, then exit
    if (!ids.length) {
      trackDigitizationStage(
        'validation',
        'blocked',
        'missing_document_ids',
        undefined,
        'missing-document-ids'
      );
      handleError(
        'Unable to start digitization. Please re-upload and try again.'
      );
      setValidationInProgress(false);
      return;
    }

    // Step 7: Set the validation lock so only one run happens at a time
    validateLockRef.current = true;

    try {
      // Step 8: Build the validation PAYLOAD based on tag
      // Includes financial year(s) for financials or tax_id for COI/tax returns
      const validationData = (() => {
        const currentYear = new Date().getFullYear();
        if (tag === DOCUMENT_TAGS.PROVISIONAL_FINANCIALS) {
          return { expected_year: String(currentYear) };
        }
        if (tag === DOCUMENT_TAGS.FINANCIALS) {
          const expectedStartYear = currentYear - 2;
          const expectedEndYear = currentYear - 1;
          return {
            expected_start_year: String(expectedStartYear),
            expected_end_year: String(expectedEndYear),
          };
        }
        if (tag === DOCUMENT_TAGS.COI || tag === DOCUMENT_TAGS.TAX_RETURNS) {
          return { tax_id: einTrim };
        }
        return {};
      })();

      // Step 9: Track the start of validation processing
      trackDigitizationStage('validation', 'started', 'processing', {
        docCount: ids.length,
        payload: { documentCount: ids.length },
      });

      // Step 10: Start backend validation via API call
      const res = await postValidateDocuments(leadId, {
        document_ids: ids,
        data: validationData,
      });

      if (onValidationFired) {
        onValidationFired(true);
      }

      // Step 11: Track that validation completed successfully
      trackDigitizationStage('validation', 'completed', 'succeeded', {
        batchId: res.batch_id,
        docCount: ids.length,
        payload: { documentCount: ids.length },
      });

      // Step 12: Track start of polling for status (batch-level)
      trackDigitizationStage('poll', 'started', 'initiated', {
        batchId: res.batch_id,
      });

      // Step 13: Wait briefly before polling for digitization status
      await wait(COI_POLL_INITIAL_DELAY_MS);

      // Step 14: Poll backend for latest digitization status until terminal state or timeout
      const startedAt = Date.now();
      while (Date.now() - startedAt <= COI_POLL_MAX_MS) {
        // Fetch current batch status and update local state
        const data = await getDigitizationStatus(leadId, res.batch_id);
        const st = String(data?.status || '')
          .trim()
          .toLowerCase();
        setPolledStatus(st);
        setPolledRejectionReasons(normalizeReasons(data?.rejection_reasons));
        await refetch();

        // Check if we've reached a terminal API state
        const isTerminal =
          st === DIGITIZATION_API_STATES.VERIFIED ||
          st === DIGITIZATION_API_STATES.COMPLETED ||
          st === DIGITIZATION_API_STATES.VALIDATION_FAILED ||
          st === DIGITIZATION_API_STATES.FAILED;
        if (isTerminal) {
          // Step 15: Track poll completion if terminal
          trackDigitizationStage('poll', 'completed', 'terminal', {
            batchId: res.batch_id,
            status: st,
          });
          break;
        }
        // Step 16: Otherwise, wait before polling again
        await wait(COI_POLL_INTERVAL_MS);
      }

      // Step 17: If loop timed out, track timeout and show error
      if (Date.now() - startedAt > COI_POLL_MAX_MS) {
        trackDigitizationStage('poll', 'completed', 'timeout', {
          batchId: res.batch_id,
        });
        handleError(t('us_digitization_failed_hint'));
      }
    } catch {
      // Step 18: In case of any error, track failure and show error
      trackDigitizationStage(
        'validation',
        'completed',
        'failed',
        undefined,
        'validation-or-poll-failed'
      );
      handleError(t('us_digitization_failed_hint'));
    } finally {
      // Step 19: Always release the lock and end validation state, re-pull latest data
      validateLockRef.current = false;
      setValidationInProgress(false);
      void refetch();

      if (onValidationFired) {
        onValidationFired(true);
      }
    }
  }, [
    ein,
    enableValidation,
    leadId,
    refetch,
    resolveTaggedIdsWithRetries,
    tag,
    t,
    trackDigitizationStage,
    wait,
    onValidationFired,
  ]);

  const statusTrim = (digitizationStatus || '').trim();
  const statusLc = statusTrim.toLowerCase();

  useEffect(() => {
    latestFilesRef.current = files || [];
    setVisibleFiles(files || []);
  }, [filesSignature, files]);

  const handleBatchUploadStart = useCallback(async () => {
    // Re-upload UX: clear current docs in frontend state immediately.
    setVisibleFiles([]);
    setPolledStatus('');
    setPolledRejectionReasons([]);
    trackDigitizationStage('upload', 'cleanup', 'started');
    try {
      await onBatchUploadStart?.();
      trackDigitizationStage('upload', 'cleanup', 'succeeded');
    } catch {
      trackDigitizationStage('upload', 'cleanup', 'failed');
      throw new Error('cleanup-failed');
    }
  }, [onBatchUploadStart, trackDigitizationStage]);

  const uiState = (() => {
    if (
      statusLc === DIGITIZATION_API_STATES.VERIFIED ||
      statusLc === DIGITIZATION_API_STATES.COMPLETED
    ) {
      return DIGITIZATION_UI_STATES.VERIFIED;
    }
    if (
      statusLc === DIGITIZATION_API_STATES.VALIDATION_FAILED ||
      statusLc === DIGITIZATION_API_STATES.FAILED ||
      statusTrim === CLIENT_DIGITIZATION_FAILED
    ) {
      return DIGITIZATION_UI_STATES.ERROR;
    }
    if (
      statusLc === DIGITIZATION_API_STATES.PENDING ||
      statusLc === DIGITIZATION_API_STATES.UNDER_REVIEW ||
      statusLc === DIGITIZATION_API_STATES.IN_PROGRESS
    ) {
      return DIGITIZATION_UI_STATES.UNDER_REVIEW;
    }
    return DIGITIZATION_UI_STATES.IDLE;
  })();

  const chipLabel = (() => {
    if (uiState === DIGITIZATION_UI_STATES.VERIFIED) {
      return t('us_digitization_status_verified');
    }
    if (uiState === DIGITIZATION_UI_STATES.UNDER_REVIEW) {
      return t('us_digitization_status_under_review');
    }
    if (statusLc === DIGITIZATION_API_STATES.VALIDATION_FAILED) {
      return t('us_digitization_status_validation_failed');
    }
    if (
      statusLc === DIGITIZATION_API_STATES.FAILED ||
      statusTrim === CLIENT_DIGITIZATION_FAILED
    ) {
      return t('us_digitization_status_failed');
    }
    return t('us_digitization_status_pending');
  })();

  const chipColor = (() => {
    if (uiState === DIGITIZATION_UI_STATES.VERIFIED) return 'success' as const;
    if (uiState === DIGITIZATION_UI_STATES.UNDER_REVIEW)
      return 'warning' as const;
    if (uiState === DIGITIZATION_UI_STATES.ERROR) return 'error' as const;
    return 'info' as const;
  })();

  const handleUploadProgress = useCallback((progressEvent: any) => {
    const percentCompleted = Math.round(
      (progressEvent.loaded * 100) / progressEvent.total
    );
    setUploadProgress(percentCompleted);
  }, []);

  const uploadSingleFile = useCallback(
    async (file: File) => {
      const headers = {
        Authorization: `Bearer ${cookies[TOKEN]}`,
      };
      const formData = new FormData();
      formData.append('file', file);
      await UploadDocument(
        uploadUrl,
        'POST',
        headers,
        formData,
        handleUploadProgress
      );
    },
    [cookies, handleUploadProgress, uploadUrl]
  );

  const handleFilesSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (
        !e.target?.files?.length ||
        disabled ||
        isUploading ||
        isValidationInProgress
      )
        return;
      trackDigitizationStage('upload', 'cta', 'clicked');
      onTrackUpload?.();
      setValidationInProgress(true);

      const filesUploaded = Array.from(e.target.files);
      const underscoredFiles = filesUploaded.map((file) =>
        renameFile(file, file?.name?.replaceAll(' ', '_'))
      );
      const validFiles: File[] = [];
      underscoredFiles.forEach((file) => {
        const extension = file?.name?.split('.')?.pop() || '';
        const supported = supportedFiles.includes(extension.toLowerCase());
        if (!supported || file.size > fileLimit) {
          const msg = t('phrase_file_upload_rule', {
            sizeLimit: humanFileSize(fileLimit, true, 0),
          });
          // setLocalError(msg);
          handleError(msg, {}, 8000);
        } else {
          validFiles.push(file);
        }
      });
      if (!validFiles.length) {
        setValidationInProgress(false);
        return;
      }
      // setLocalError('');

      if (onValidationFired) {
        onValidationFired(false);
      }

      try {
        await handleBatchUploadStart();
      } catch {
        handleError('Unable to clean up existing documents. Please retry.');
        setValidationInProgress(false);
        e.target.value = '';
        return;
      }
      setUploading(true);
      setUploadProgress(0);
      try {
        trackDigitizationStage('upload', 'started', 'processing', {
          docCount: validFiles.length,
          payload: { fileCount: validFiles.length },
        });
        for (let i = 0; i < validFiles.length; i++) {
          await uploadSingleFile(validFiles[i]);
        }
        trackDigitizationStage('upload', 'completed', 'succeeded', {
          docCount: validFiles.length,
          payload: { fileCount: validFiles.length },
        });
      } catch {
        trackDigitizationStage(
          'upload',
          'completed',
          'failed',
          undefined,
          'upload-failed'
        );
        handleError(t('title_file_error_message'));
        setValidationInProgress(false);
        e.target.value = '';
        return;
      } finally {
        setUploading(false);
      }
      void runValidationByTag().catch(() => {
        setValidationInProgress(false);
      });
      const refetchResult = refetch();
      if (refetchResult && typeof refetchResult.catch === 'function') {
        refetchResult.catch(() => {
          handleError(
            'Files uploaded successfully. We could not refresh status, but validation will continue.'
          );
        });
      }

      e.target.value = '';
    },
    [
      disabled,
      fileLimit,
      handleBatchUploadStart,
      isUploading,
      isValidationInProgress,
      onTrackUpload,
      refetch,
      runValidationByTag,
      supportedFiles,
      t,
      trackDigitizationStage,
      uploadSingleFile,
      onValidationFired,
    ]
  );

  const openOrDownload = (file: UploadFile) => {
    if (file?.url) {
      window.open(file.url, '_blank', 'noopener,noreferrer');
      return;
    }
    saveAs(file?.url || '', file?.name || '');
  };

  const removeFromFrontend = (file: UploadFile) => {
    setVisibleFiles((prev) =>
      prev.filter((x) =>
        x.documentId ? x.documentId !== file.documentId : x.name !== file.name
      )
    );
  };

  const removeFileFromBackend = useCallback(
    async (file: UploadFile) => {
      if (!file?.documentId) {
        removeFromFrontend(file);
        return;
      }
      try {
        await removeFileTag({
          variables: {
            input: {
              documentId: String(file.documentId),
              id: String(leadId || ''),
            },
          },
        });
        removeFromFrontend(file);
        await refetch();
      } catch {
        handleError(t('title_file_remove_message'));
      }
    },
    [leadId, refetch, removeFileTag, t]
  );

  const deleteIconMode = (
    file: UploadFile
  ): 'enabled' | 'disabled' | 'hidden' => {
    if (disableDeleteActions) return 'disabled';
    const st = (
      file as UploadFile & { digitization?: { status?: string | null } }
    )?.digitization?.status;
    const lc = String(st || '')
      .trim()
      .toLowerCase();
    if (
      lc === DIGITIZATION_API_STATES.UNDER_REVIEW ||
      lc === DIGITIZATION_API_STATES.PENDING ||
      lc === DIGITIZATION_API_STATES.IN_PROGRESS
    ) {
      return 'disabled';
    }
    if (
      lc === DIGITIZATION_API_STATES.VERIFIED ||
      lc === DIGITIZATION_API_STATES.COMPLETED ||
      lc === DIGITIZATION_API_STATES.FAILED ||
      lc === DIGITIZATION_API_STATES.VALIDATION_FAILED
    ) {
      if (
        disableDeleteForVerified &&
        (lc === DIGITIZATION_API_STATES.VERIFIED ||
          lc === DIGITIZATION_API_STATES.COMPLETED)
      ) {
        return 'disabled';
      }
      return 'enabled';
    }
    return 'hidden';
  };

  const renderInlineValidationFailed = () => {
    const effectiveStatusLc = (polledStatus || statusLc).toLowerCase();
    const reasons = normalizeReasons(
      (polledRejectionReasons || []).length
        ? polledRejectionReasons
        : validationFailedReasons
    );
    const isFailureStatus =
      effectiveStatusLc === DIGITIZATION_API_STATES.VALIDATION_FAILED ||
      effectiveStatusLc === DIGITIZATION_API_STATES.FAILED;
    if (!isFailureStatus) return null;
    if (!reasons.length) return null;

    return (
      <Alert
        severity="error"
        title={t('us_digitization_review_document_title')}
      >
        {reasons.length > 1 && tag === DOCUMENT_TAGS.BANK_STATEMENT ? (
          <ul style={{ paddingLeft: '24px', margin: 0 }}>
            {reasons.map((reason, idx) => (
              <li key={idx}>
                <Typography
                  variant="body-small"
                  color="secondary"
                  _sx={{ paddingLeft: '8px' }}
                >
                  {reason}
                </Typography>
              </li>
            ))}
          </ul>
        ) : (
          <>
            {reasons.length > 0 ? (
              <Typography
                variant="body-small"
                color="secondary"
                _sx={{ paddingLeft: '8px' }}
              >
                {reasons.join(' ')}
              </Typography>
            ) : null}
          </>
        )}
      </Alert>
    );
  };

  const sectionCardSx = {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: DAJ_COLORS.SKY_BLUE_400 as ColorEnum,
  };

  const isUploadDisabledForRole = isBDM;

  useEffect(() => {
    if (onProcessingStateChange) {
      onProcessingStateChange(isUploading || isValidationInProgress);
    }
  }, [isUploading, isValidationInProgress, onProcessingStateChange]);

  useEffect(() => {
    if (
      autoValidateIfMissingBatchId &&
      visibleFiles.length > 0 &&
      !batchId &&
      !isValidationInProgress &&
      !isUploading &&
      !validateLockRef.current
    ) {
      setValidationInProgress(true);
      void runValidationByTag().catch(() => {
        setValidationInProgress(false);
      });
    }
  }, [
    autoValidateIfMissingBatchId,
    visibleFiles.length,
    batchId,
    isValidationInProgress,
    isUploading,
    runValidationByTag,
  ]);

  const hasInlineAlert = (() => {
    const effectiveStatusLc = (polledStatus || statusLc).toLowerCase();
    const reasons = normalizeReasons(
      (polledRejectionReasons || []).length
        ? polledRejectionReasons
        : validationFailedReasons
    );
    const isFailureStatus =
      effectiveStatusLc === DIGITIZATION_API_STATES.VALIDATION_FAILED ||
      effectiveStatusLc === DIGITIZATION_API_STATES.FAILED;
    return isFailureStatus && reasons.length > 0;
  })();
  const effectiveShowReviewProgress =
    showReviewProgress || isValidationInProgress;

  return (
    <Paper
      dataTestId={`${resolvedTestIdPrefix}-root`}
      _sx={sectionCardSx}
      variant="outlined"
      elevation={0}
    >
      <Box _sx={{ padding: 2 }}>
        <Flex
          dataTestId={`${resolvedTestIdPrefix}-header`}
          justifyContent="space-between"
          alignItems={'center'}
          spacing={2}
        >
          <Box _sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
            <Flex
              spacing={1}
              alignItems="center"
              _sx={{
                width: '100%',
                cursor: showChevron ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (!showChevron) return;
                setHeaderAccordionOpen((prev) => !prev);
              }}
            >
              {showChevron ? (
                <ExpandMore
                  sx={{
                    color: DAJ_COLORS.WEALTHY_GREEN,
                    fontSize: 26,
                    flexShrink: 0,
                    transform: isHeaderAccordionOpen
                      ? 'rotate(0deg)'
                      : 'rotate(-90deg)',
                    transition: 'transform 0.2s ease',
                    ':hover': {
                      borderRadius: '8px',
                      background: DAJ_COLORS.WEALTHY_GREEN_50,
                    },
                  }}
                  aria-hidden
                />
              ) : null}
              <Box _sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="secondary"
                  _sx={{
                    width: 'fit-content',
                    lineHeight: 1.25,
                  }}
                >
                  {title}
                </Typography>
              </Box>
            </Flex>
          </Box>
          <Flex alignItems="center" spacing={2}>
            <Flex justifyContent="center">
              <Chip
                dataTestId={`${resolvedTestIdPrefix}-status-chip`}
                size="small"
                variant="filled"
                color={chipColor}
                label={
                  <Typography variant="validation">{chipLabel}</Typography>
                }
                _sx={{
                  width: 'max-content',
                }}
              />
            </Flex>
            <AtomicButton
              dataTestId={`${resolvedTestIdPrefix}-upload-button`}
              variant={ButtonTypeEnum.OUTLINED}
              disabled={
                isUploadDisabledForRole ||
                disabled ||
                isUploading ||
                isValidationInProgress ||
                (visibleFiles && visibleFiles.length >= maxFiles)
              }
              onClick={(e) => {
                if (
                  enableReuploadForVerified &&
                  uiState === DIGITIZATION_UI_STATES.VERIFIED
                ) {
                  // If the user clicked the actual input, don't intercept it
                  if ((e.target as HTMLElement).tagName === 'INPUT') {
                    return;
                  }
                  e.preventDefault();
                  setConfirmModalOpen(true);
                }
              }}
              _sx={{
                paddingX: '16px',
                paddingY: '12px',
                textAlign: 'center',
                minWidth: '180px',
                height: '40px',
              }}
              component={'label'}
            >
              {buttonTitle}
              <input
                id={`${resolvedTestIdPrefix}-input-${
                  normalizedTag || 'unknown'
                }`}
                data-testid={`${resolvedTestIdPrefix}-file-input`}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFilesSelected}
                multiple={true}
                autoComplete="off"
                disabled={
                  isUploadDisabledForRole ||
                  disabled ||
                  isUploading ||
                  isValidationInProgress
                }
                hidden
              />
            </AtomicButton>
          </Flex>
        </Flex>
        <Flex>
          <Box
            _sx={{
              width: '30px',
            }}
          ></Box>
          {isHeaderAccordionOpen &&
          (descriptionItemsTitle || descriptionItems.length > 0) ? (
            <Box>
              <Spacer verticalSpace={1} />
              {descriptionItemsTitle ? (
                <Typography
                  variant="body"
                  fontWeight={descriptionItemsRegular ? 'regular' : 'bold'}
                  _sx={{ marginBottom: 0.5 }}
                >
                  {descriptionItemsTitle}
                </Typography>
              ) : null}
              <ul style={{ margin: 0, paddingLeft: 24 }}>
                {descriptionItems.map((item) => (
                  <li key={item}>
                    <Typography variant="label">{item}&nbsp;</Typography>
                    {descriptionItemsLinkNode ? descriptionItemsLinkNode : null}
                  </li>
                ))}
              </ul>
              {normalizedTag === DOCUMENT_TAGS.FINANCIALS ? (
                <Typography
                  variant="label"
                  fontWeight="bold"
                  _sx={{ marginTop: 1 }}
                >
                  Note:
                  <br />
                  1. If you have multiple documents, please upload them all
                  together.
                  <br />
                  2. Please provide audited financials if available.
                </Typography>
              ) : (
                <Typography
                  variant="label"
                  fontWeight="bold"
                  _sx={{ marginTop: 1 }}
                >
                  Note: If you have multiple documents, please upload them all
                  together.
                </Typography>
              )}
            </Box>
          ) : null}
        </Flex>

        {visibleFiles.length > 0 ? <Spacer verticalSpace={2} /> : null}
        {visibleFiles.map((file, idx) => (
          <React.Fragment key={`${file.documentId || file.name || idx}`}>
            <Flex
              dataTestId={`${resolvedTestIdPrefix}-file-row`}
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Flex
                alignItems="center"
                spacing={1}
                _sx={{ paddingLeft: '30px' }}
              >
                <FolderOpen />
                <Typography variant="body">{file.name}</Typography>
              </Flex>
              <Flex alignItems="center" spacing={1}>
                <Flex
                  _sx={{
                    padding: '4px',
                    borderRadius: '8px',
                    ':hover': {
                      background: DAJ_COLORS.WEALTHY_GREEN_50,
                    },
                  }}
                  onClick={() => openOrDownload(file)}
                >
                  <VisibilityOutlined
                    style={{
                      cursor: 'pointer',
                      color: COLORS.PRIMARY,
                      transition: 'color 0.2s',
                      borderRadius: 8,
                    }}
                  />
                </Flex>
                {!hideDeleteActions && deleteIconMode(file) !== 'hidden' ? (
                  <>
                    <Box _sx={{ width: '24px' }}></Box>
                    <DeleteOutlineOutlined
                      style={{
                        cursor:
                          deleteIconMode(file) === 'disabled'
                            ? 'not-allowed'
                            : 'pointer',
                        color:
                          deleteIconMode(file) === 'disabled'
                            ? DAJ_COLORS.LIGHT_GRAY
                            : COLORS.PRIMARY,
                      }}
                      onClick={() => {
                        if (deleteIconMode(file) === 'disabled') return;
                        void removeFileFromBackend(file);
                      }}
                    />
                  </>
                ) : null}
              </Flex>
            </Flex>
            {idx !== visibleFiles.length - 1 ? (
              <Spacer verticalSpace={1} />
            ) : null}
          </React.Fragment>
        ))}

        {effectiveShowReviewProgress ? <Spacer verticalSpace={1} /> : null}
        {effectiveShowReviewProgress ? (
          <Box _sx={{ marginTop: 1, marginLeft: 3.6, maxWidth: '100%' }}>
            <LinearProgress variant="indeterminate" />
            <Spacer verticalSpace={0.5} />
            <Typography variant="label" color="secondary">
              {t('us_digitization_reviewing_document')}
            </Typography>
          </Box>
        ) : null}

        {isUploading ? <Spacer verticalSpace={1} /> : null}
        {isUploading ? (
          <Flex
            alignItems="center"
            spacing={1}
            _sx={{ marginTop: 1, marginLeft: 3.6 }}
          >
            <CircularProgress size={20} thickness={4} />
            <Typography variant="label" color="secondary">
              {uploadProgress > 0
                ? `${uploadProgress}%`
                : 'Upload in progress..'}
            </Typography>
          </Flex>
        ) : null}

        {hasInlineAlert ? <Spacer verticalSpace={1} /> : null}
        {hasInlineAlert ? (
          <Box
            dataTestId={`${resolvedTestIdPrefix}-validation-alert`}
            _sx={{ marginTop: 1 }}
          >
            {renderInlineValidationFailed()}
            <Spacer verticalSpace={1} />
            <FormControl
              error={Boolean(errorMessage)}
              style={{ marginBottom: '16px' }}
            >
              <TextField
                name={reasonFieldName}
                value={currentValue}
                placeholder="Enter Reason"
                fullWidth={true}
                required={true}
                labelText="For faster approval, provide the document mentioned above. To proceed anyway, enter a reason below"
                onChange={(e) => {
                  const value = e.target.value;
                  formik?.setFieldValue(reasonFieldName, value);
                  sessionStorage.setItem(reasonFieldName, value);
                }}
                onBlur={formik?.handleBlur}
              />
              {errorMessage && (
                <FormHelperText style={{ color: 'red' }}>
                  {errorMessage as string}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        ) : null}
        <Modal
          isOpen={confirmModalOpen}
          closeDialog={() => setConfirmModalOpen(false)}
          title="Confirm Re-upload"
          content="On re-upload, all verified documents will be removed and replaced with the new document, and re-verification will be needed. Do you want to proceed?"
          buttons={[
            {
              dataTestId: 'btn_confirm_reupload',
              title: 'Yes, Proceed',
              handler: () => {
                setConfirmModalOpen(false);
                console.log(
                  `${resolvedTestIdPrefix}-input-${normalizedTag || 'unknown'}`
                );
                const tagId = `${resolvedTestIdPrefix}-input-${
                  normalizedTag || 'unknown'
                }`;
                // Small delay to allow the modal to close and prevent click interception
                setTimeout(() => {
                  console.log('tagId ran!');
                  document.getElementById(tagId)?.click();
                }, 0);
              },
              variant: ButtonTypeEnum.CONTAINED,
            },
            {
              dataTestId: 'btn_cancel_reupload',
              title: 'Cancel',
              handler: () => setConfirmModalOpen(false),
              variant: ButtonTypeEnum.OUTLINED,
            },
          ]}
        />
      </Box>
    </Paper>
  );
};

export default UploadCardV2;
